'use client';

import { c as _c } from "react/compiler-runtime";
import { type MouseEvent as ReactMouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { isPointInsideRect } from '@/app/components/image-zoom-pan-pinch/image-geometry';
import { getInlineImageSize } from '@/app/components/image-zoom-pan-pinch/image-layout';
import { BUTTON_ZOOM_ANIMATION_TIME, BUTTON_ZOOM_ANIMATION_TYPE, BUTTON_ZOOM_STEP, DRAG_THRESHOLD, INITIAL_SCALE, MAX_SCALE, MIN_SCALE, RESET_ANIMATION_TIME, RESET_ANIMATION_TYPE, SCALE_TOLERANCE } from '@/app/components/image-zoom-pan-pinch/image-zoom.constants';
import type { ImageZoomPanPinchExpose, UseImageZoomPanPinchOptions } from '@/app/components/image-zoom-pan-pinch/types';
import { cn } from '@/app/utils/cn';
interface Point {
  x: number;
  y: number;
}
const zoomControlDisabledClassName = 'cursor-default bg-transparent text-gray-600 hover:bg-transparent hover:text-gray-600 focus-visible:bg-transparent focus-visible:text-gray-600 active:bg-transparent active:text-gray-600 disabled:bg-transparent disabled:text-gray-600';
export const useImageZoomPanPinch = t0 => {
  const $ = _c(57);
  const {
    normalizedWidth,
    normalizedHeight,
    imageDataTestId,
    shouldRender,
    actionsTopRef,
    actionsBottomRef,
    stageLayoutKey: t1,
    onClickOutsideImageRef
  } = t0;
  const stageLayoutKey = t1 === undefined ? "" : t1;
  const imageStageRef = useRef(null);
  const transformRef = useRef(null);
  const scaleRafIdRef = useRef(null);
  const pointerStartRef = useRef(null);
  const skipStageClickRef = useRef(false);
  const pendingScaleRef = useRef(INITIAL_SCALE);
  const availableImageHeightRef = useRef(0);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      width: 0,
      height: 0
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const imageStageInnerSizeRef = useRef(t2);
  const [currentScale, setCurrentScale] = useState(INITIAL_SCALE);
  const [isPointerDown, setIsPointerDown] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      canPanX: false,
      canPanY: false
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [panCapability, setPanCapability] = useState(t3);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      width: 0,
      height: 0
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [inlineImageSize, setInlineImageSize] = useState(t4);
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = nextScale => {
      setCurrentScale(prevScale => Math.abs(prevScale - nextScale) <= SCALE_TOLERANCE ? prevScale : nextScale);
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const commitCurrentScale = t5;
  let t6;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = nextScale_0 => {
      pendingScaleRef.current = nextScale_0;
      if (scaleRafIdRef.current !== null) {
        return;
      }
      scaleRafIdRef.current = window.requestAnimationFrame(() => {
        scaleRafIdRef.current = null;
        commitCurrentScale(pendingScaleRef.current);
      });
    };
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  const scheduleCurrentScaleSync = t6;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = nextScale_1 => {
      if (scaleRafIdRef.current !== null) {
        window.cancelAnimationFrame(scaleRafIdRef.current);
        scaleRafIdRef.current = null;
      }
      pendingScaleRef.current = nextScale_1;
      commitCurrentScale(nextScale_1);
    };
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const syncCurrentScaleImmediately = t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      syncCurrentScaleImmediately(INITIAL_SCALE);
    };
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  const resetScaleImmediately = t8;
  const handleRecenterAtInitialScale = _temp;
  let t9;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = ref_0 => {
      const bounds = ref_0.instance.bounds;
      const canPanX = bounds ? Math.abs(bounds.maxPositionX - bounds.minPositionX) > SCALE_TOLERANCE : false;
      const canPanY = bounds ? Math.abs(bounds.maxPositionY - bounds.minPositionY) > SCALE_TOLERANCE : false;
      setPanCapability(prev => prev.canPanX === canPanX && prev.canPanY === canPanY ? prev : {
        canPanX,
        canPanY
      });
    };
    $[7] = t9;
  } else {
    t9 = $[7];
  }
  const updatePanCapability = t9;
  const isZoomOutDisabled = currentScale <= MIN_SCALE + SCALE_TOLERANCE;
  const isZoomInDisabled = currentScale >= MAX_SCALE - SCALE_TOLERANCE;
  const isResetDisabled = Math.abs(currentScale - INITIAL_SCALE) <= SCALE_TOLERANCE;
  const isPanningDisabled = currentScale <= INITIAL_SCALE + SCALE_TOLERANCE;
  const canPan = !isPanningDisabled;
  const imageCursorClassName = !canPan ? "cursor-default" : isDragging ? "cursor-move" : "cursor-default";
  const getZoomControlButtonClassName = _temp2;
  let t10;
  if ($[8] !== normalizedHeight || $[9] !== normalizedWidth) {
    t10 = () => {
      setInlineImageSize(getInlineImageSize({
        imageWidth: normalizedWidth,
        imageHeight: normalizedHeight,
        stageWidth: imageStageInnerSizeRef.current.width,
        stageHeight: imageStageInnerSizeRef.current.height,
        availableHeight: availableImageHeightRef.current
      }));
    };
    $[8] = normalizedHeight;
    $[9] = normalizedWidth;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const updateInlineImageSize = t10;
  let t11;
  if ($[11] !== updateInlineImageSize) {
    t11 = () => {
      const stageElement = imageStageRef.current;
      if (!stageElement) {
        return;
      }
      const updateImageStageInnerSize = () => {
        const nextSize = {
          width: stageElement.clientWidth,
          height: stageElement.clientHeight
        };
        imageStageInnerSizeRef.current = nextSize;
        updateInlineImageSize();
      };
      updateImageStageInnerSize();
      const observer = new ResizeObserver(() => {
        updateImageStageInnerSize();
      });
      observer.observe(stageElement);
      return () => {
        observer.disconnect();
      };
    };
    $[11] = updateInlineImageSize;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== shouldRender || $[14] !== stageLayoutKey || $[15] !== updateInlineImageSize) {
    t12 = [shouldRender, stageLayoutKey, updateInlineImageSize];
    $[13] = shouldRender;
    $[14] = stageLayoutKey;
    $[15] = updateInlineImageSize;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  useEffect(t11, t12);
  let t13;
  if ($[17] !== actionsBottomRef || $[18] !== actionsTopRef || $[19] !== updateInlineImageSize) {
    t13 = () => {
      const topElement = actionsTopRef.current;
      const bottomElement = actionsBottomRef.current;
      if (!topElement || !bottomElement) {
        return;
      }
      const updateAvailableImageHeight = () => {
        const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
        const topHeight = topElement.getBoundingClientRect().height;
        const bottomHeight = bottomElement.getBoundingClientRect().height;
        const nextHeight = Math.max(0, Math.floor(viewportHeight - topHeight - bottomHeight));
        availableImageHeightRef.current = nextHeight;
        updateInlineImageSize();
      };
      updateAvailableImageHeight();
      const observer_0 = new ResizeObserver(() => {
        updateAvailableImageHeight();
      });
      observer_0.observe(topElement);
      observer_0.observe(bottomElement);
      window.addEventListener("resize", updateAvailableImageHeight);
      window.visualViewport?.addEventListener("resize", updateAvailableImageHeight);
      return () => {
        observer_0.disconnect();
        window.removeEventListener("resize", updateAvailableImageHeight);
        window.visualViewport?.removeEventListener("resize", updateAvailableImageHeight);
      };
    };
    $[17] = actionsBottomRef;
    $[18] = actionsTopRef;
    $[19] = updateInlineImageSize;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== actionsBottomRef || $[22] !== actionsTopRef || $[23] !== shouldRender || $[24] !== updateInlineImageSize) {
    t14 = [actionsBottomRef, actionsTopRef, shouldRender, updateInlineImageSize];
    $[21] = actionsBottomRef;
    $[22] = actionsTopRef;
    $[23] = shouldRender;
    $[24] = updateInlineImageSize;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[26] !== canPan) {
    t15 = () => {
      if (!canPan) {
        queueMicrotask(() => {
          setIsPointerDown(false);
          setIsDragging(false);
        });
      }
    };
    t16 = [canPan];
    $[26] = canPan;
    $[27] = t15;
    $[28] = t16;
  } else {
    t15 = $[27];
    t16 = $[28];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => () => {
      if (scaleRafIdRef.current !== null) {
        window.cancelAnimationFrame(scaleRafIdRef.current);
        scaleRafIdRef.current = null;
      }
    };
    t18 = [];
    $[29] = t17;
    $[30] = t18;
  } else {
    t17 = $[29];
    t18 = $[30];
  }
  useEffect(t17, t18);
  let t19;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = () => {
      transformRef.current?.zoomOut(BUTTON_ZOOM_STEP, BUTTON_ZOOM_ANIMATION_TIME, BUTTON_ZOOM_ANIMATION_TYPE);
    };
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  const handleZoomOut = t19;
  let t20;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => {
      transformRef.current?.zoomIn(BUTTON_ZOOM_STEP, BUTTON_ZOOM_ANIMATION_TIME, BUTTON_ZOOM_ANIMATION_TYPE);
    };
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  const handleZoomIn = t20;
  let t21;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => {
      transformRef.current?.resetTransform(RESET_ANIMATION_TIME, RESET_ANIMATION_TYPE);
    };
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  const handleResetZoom = t21;
  let t22;
  if ($[34] !== canPan) {
    t22 = () => {
      if (!canPan) {
        return;
      }
      skipStageClickRef.current = false;
      pointerStartRef.current = null;
      setIsPointerDown(true);
      setIsDragging(false);
    };
    $[34] = canPan;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  const handlePointerDown = t22;
  let t23;
  if ($[36] !== canPan || $[37] !== isPointerDown) {
    t23 = event => {
      if (!canPan || !isPointerDown) {
        return;
      }
      if (!pointerStartRef.current) {
        pointerStartRef.current = {
          x: event.clientX,
          y: event.clientY
        };
        return;
      }
      const deltaX = event.clientX - pointerStartRef.current.x;
      const deltaY = event.clientY - pointerStartRef.current.y;
      const distance = Math.hypot(deltaX, deltaY);
      if (distance < DRAG_THRESHOLD) {
        return;
      }
      skipStageClickRef.current = true;
      setIsDragging(true);
    };
    $[36] = canPan;
    $[37] = isPointerDown;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  const handlePointerMove = t23;
  let t24;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => {
      pointerStartRef.current = null;
      setIsPointerDown(false);
      setIsDragging(false);
    };
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  const handlePointerEnd = t24;
  let t25;
  if ($[40] !== imageDataTestId || $[41] !== onClickOutsideImageRef) {
    t25 = event_0 => {
      if (skipStageClickRef.current) {
        skipStageClickRef.current = false;
        return;
      }
      const stageElement_0 = imageStageRef.current;
      if (!stageElement_0) {
        return;
      }
      const imageElement = stageElement_0.querySelector(`[data-test="${imageDataTestId}"]`);
      if (!imageElement) {
        return;
      }
      const imageRect = imageElement.getBoundingClientRect();
      if (imageRect.width <= 0 || imageRect.height <= 0) {
        return;
      }
      if (!isPointInsideRect(event_0.clientX, event_0.clientY, imageRect)) {
        onClickOutsideImageRef.current?.();
      }
    };
    $[40] = imageDataTestId;
    $[41] = onClickOutsideImageRef;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  const handleStageClickCapture = t25;
  const t26 = !panCapability.canPanX;
  const t27 = !panCapability.canPanY;
  let t28;
  if ($[43] !== isPanningDisabled || $[44] !== t26 || $[45] !== t27) {
    t28 = {
      disabled: isPanningDisabled,
      lockAxisX: t26,
      lockAxisY: t27,
      velocityDisabled: true
    };
    $[43] = isPanningDisabled;
    $[44] = t26;
    $[45] = t27;
    $[46] = t28;
  } else {
    t28 = $[46];
  }
  const panning = t28;
  let t29;
  if ($[47] !== handlePointerDown || $[48] !== handlePointerMove || $[49] !== handleStageClickCapture || $[50] !== imageCursorClassName || $[51] !== inlineImageSize || $[52] !== isResetDisabled || $[53] !== isZoomInDisabled || $[54] !== isZoomOutDisabled || $[55] !== panning) {
    t29 = {
      transformRef,
      imageStageRef,
      handleStageClickCapture,
      inlineImageSize,
      isZoomOutDisabled,
      isZoomInDisabled,
      isResetDisabled,
      getZoomControlButtonClassName,
      handleZoomOut,
      handleZoomIn,
      handleResetZoom,
      resetScaleImmediately,
      _panning: panning,
      _imageCursorClassName: imageCursorClassName,
      _syncCurrentScaleImmediately: syncCurrentScaleImmediately,
      _scheduleCurrentScaleSync: scheduleCurrentScaleSync,
      _handleRecenterAtInitialScale: handleRecenterAtInitialScale,
      _updatePanCapability: updatePanCapability,
      _handlePointerDown: handlePointerDown,
      _handlePointerMove: handlePointerMove,
      _handlePointerEnd: handlePointerEnd
    };
    $[47] = handlePointerDown;
    $[48] = handlePointerMove;
    $[49] = handleStageClickCapture;
    $[50] = imageCursorClassName;
    $[51] = inlineImageSize;
    $[52] = isResetDisabled;
    $[53] = isZoomInDisabled;
    $[54] = isZoomOutDisabled;
    $[55] = panning;
    $[56] = t29;
  } else {
    t29 = $[56];
  }
  return t29;
};
function _temp(ref) {
  const {
    scale,
    positionX,
    positionY
  } = ref.state;
  const isAtInitialScale = scale <= INITIAL_SCALE + SCALE_TOLERANCE;
  const isOffCenter = Math.abs(positionX) > SCALE_TOLERANCE || Math.abs(positionY) > SCALE_TOLERANCE;
  if (isAtInitialScale && isOffCenter) {
    ref.resetTransform(RESET_ANIMATION_TIME, RESET_ANIMATION_TYPE);
  }
}
function _temp2(isDisabled) {
  return cn("pointer-events-auto", {
    [zoomControlDisabledClassName]: isDisabled
  });
}